import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FilterService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TransactionService,
  Transaction,
  TransactionResponse,
  TransactionCategory,
  USER_ATTRIBUTED_CATEGORIES,
} from '../services/transaction.service';
import { TenantService } from '../services/tenant.service';
import {
  DateRangeService,
  DurationOption,
} from '../services/date-range.service';
import { TableModule, Table } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { Subject, EMPTY, merge, takeUntil } from 'rxjs';
import { expand } from 'rxjs/operators';
import dayjs from 'dayjs';
import { convertTransactionsToCSV, downloadCSV } from '../utils/csv-utils';
import { formatJson } from '../utils/json-utils';

interface Tenant {
  tenantId: string;
}

interface ColumnConfig {
  field: string;
  header: string;
  sortField?: string;
  cssClass: string;
  filterType?: 'text' | 'numeric' | 'date' | 'multiselect' | 'boolean';
  pipe?: 'currency';
  pipeArgs?: any[];
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    MultiSelectModule,
    ToastModule,
    TooltipModule,
    SelectButtonModule,
    CheckboxModule,
  ],
  providers: [MessageService],
})
export class HomeComponent implements OnInit, OnDestroy {
  clear(dt: Table): void {
    dt.clear();
    this.transactionIdFilter = '';
    this.sourceFilterValue = [];
    this.destinationFilterValue = [];
    this.approvedFilterValue = null;
  }
  sourceOptions = [
    { label: 'Terminal', value: 'terminal' },
    { label: 'Terminal-Handler', value: 'terminal-handler' },
    { label: 'Host', value: 'host' },
    { label: 'Host-Handler', value: 'host-handler' },
  ];
  destinationOptions = [
    { label: 'Terminal', value: 'terminal' },
    { label: 'Terminal-Handler', value: 'terminal-handler' },
    { label: 'Host', value: 'host' },
    { label: 'Host-Handler', value: 'host-handler' },
  ];
  approvedOptions = [
    { label: 'Approved', value: true },
    { label: 'Declined', value: false },
  ];

  timestampDateColumnFilterOptions = [
    { label: 'Date & Time After', value: 'dateTimeAfter' },
    { label: 'Date & Time Before', value: 'dateTimeBefore' },
  ];

  sourceFilterValue: string[] = [];
  destinationFilterValue: string[] = [];
  approvedFilterValue: boolean | null = null;

  readonly columns: ColumnConfig[] = [
    {
      field: 'transactionId',
      header: 'Transaction ID',
      cssClass: 'col-medium',
      filterType: 'text',
    },
    {
      field: 'tenantId',
      header: 'Tenant',
      cssClass: 'col-base',
      filterType: 'text',
    },
    {
      field: 'isoTimestamp',
      header: 'Timestamp',
      sortField: 'epochNanoSeconds',
      cssClass: 'col-large',
      filterType: 'date',
    },
    {
      field: 'merchantTerminalId',
      header: 'Merchant-TID',
      cssClass: 'col-medium',
      filterType: 'text',
    },
    {
      field: 'hostTerminalId',
      header: 'Host-TID',
      cssClass: 'col-base',
      filterType: 'text',
    },
    {
      field: 'messageFrom',
      header: 'Source',
      cssClass: 'col-medium',
      filterType: 'multiselect',
    },
    {
      field: 'messageTo',
      header: 'Destination',
      cssClass: 'col-medium',
      filterType: 'multiselect',
    },
    {
      field: 'transactionType',
      header: 'Transaction Type',
      cssClass: 'col-large',
      filterType: 'text',
    },
    {
      field: 'originCategoryLabel',
      header: 'Originated From',
      cssClass: 'col-medium',
      filterType: 'text',
    },
    {
      field: 'userEmail',
      header: 'User',
      cssClass: 'col-large',
      filterType: 'text',
    },
    {
      field: 'hostUrl',
      header: 'Host URL',
      cssClass: 'col-medium',
      filterType: 'text',
    },
    {
      field: 'hostPort',
      header: 'Host Port',
      cssClass: 'col-base',
      filterType: 'text',
    },
    {
      field: 'pan4',
      header: 'PAN4',
      cssClass: 'col-base col-numeric',
      filterType: 'text',
    },
    {
      field: 'total',
      header: 'Total',
      cssClass: 'col-base col-total',
      filterType: 'numeric',
      pipe: 'currency',
      pipeArgs: ['USD', 'symbol', '1.2-2'],
    },
    {
      field: 'hostResponseCodeText',
      header: 'Status',
      cssClass: 'col-base',
      filterType: 'boolean',
    },
    {
      field: 'hostResponseCode',
      header: 'Response Code',
      cssClass: 'col-medium col-numeric',
      filterType: 'text',
    },
    {
      field: 'processor',
      header: 'Host',
      cssClass: 'col-base',
      filterType: 'text',
    },
    {
      field: 'sequenceNumber',
      header: 'Txn. Seq. No.',
      cssClass: 'col-medium col-numeric',
      filterType: 'text',
    },
  ];

  @ViewChild('dt') dt!: Table;

  transactions: Transaction[] = [];

  loading = false;
  loadingMore = false;

  expandedRows: { [key: string]: boolean } = {};
  transactionIdFilter: string = '';

  durationOptions: DurationOption[];
  selectedDuration: string = 'today';
  fromDate: Date;
  toDate: Date;
  maxFromDate: Date;
  maxToDate: Date;

  tenants: Tenant[] = [];
  selectedTenant: string = '';
  selectedCategories: TransactionCategory[] = ['TERMINAL_ACTIVITY'];
  multiSelectMode = false;
  private previousCategories: TransactionCategory[] = [...this.selectedCategories];
  categoryOptions: { label: string; value: TransactionCategory }[] = [
    { label: 'Terminal Activity', value: 'TERMINAL_ACTIVITY' },
    { label: 'System Generated', value: 'SYSTEM_GENERATED' },
    { label: 'Admin Audit Trails', value: 'ADMIN_AUDIT' },
  ];
  loadingTenants = false;
  tenantsFetchFailed = false;

  private destroy$ = new Subject<void>();
  private cancelLoad$ = new Subject<void>();

  constructor(
    private transactionService: TransactionService,
    private tenantService: TenantService,
    private dateRangeService: DateRangeService,
    private messageService: MessageService,
    private filterService: FilterService,
  ) {
    this.durationOptions = this.dateRangeService.durationOptions;
    const initialRange = this.dateRangeService.calculateDateRange(
      this.selectedDuration,
    );
    this.fromDate = initialRange.fromDate;
    this.toDate = initialRange.toDate;
    this.maxFromDate = initialRange.maxFromDate;
    this.maxToDate = initialRange.maxToDate;
  }

  get visibleColumns(): ColumnConfig[] {
    return this.columns.filter((col) => {
      // User column only when the selection can contain user-attributed rows
      if (col.field === 'userEmail')
        return this.selectedCategories.some((c) =>
          USER_ATTRIBUTED_CATEGORIES.includes(c),
        );
      return true;
    });
  }

  get globalFilterFields(): string[] {
    return this.visibleColumns.map((col) => col.field);
  }

  ngOnInit() {
    this.updateDatesForDuration();
    this.fetchTenants();

    this.filterService.register('dateTimeAfter', (value: Date, filter: Date) =>
      !value || !filter ? true : value.getTime() > filter.getTime(),
    );

    this.filterService.register(
      'dateTimeBefore',
      (value: Date, filter: Date) =>
        !value || !filter ? true : value.getTime() < filter.getTime(),
    );
  }

  private fetchTenants(): void {
    this.loadingTenants = true;
    this.tenantsFetchFailed = false;

    this.tenantService
      .getTenantsIds()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tenantIds: string[]) => {
          this.tenants = tenantIds.map((tenantId) => ({ tenantId }));
          this.loadingTenants = false;
          this.tenantsFetchFailed = false;
        },
        error: (error) => {
          console.error('Error fetching tenants:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load tenants. Please try again.',
          });
          this.loadingTenants = false;
          this.tenantsFetchFailed = true;
        },
      });
  }

  retryFetchTenants(): void {
    this.fetchTenants();
  }

  ngOnDestroy() {
    this.cancelLoad$.next();
    this.cancelLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }


  get isCustomRange(): boolean {
    return this.dateRangeService.isCustomRange(this.selectedDuration);
  }

  get isDatePickerDisabled(): boolean {
    return this.dateRangeService.isDatePickerDisabled(this.selectedDuration);
  }

  onDurationChange(): void {
    this.updateDatesForDuration();
  }

  private updateDatesForDuration(): void {
    const range = this.dateRangeService.calculateDateRange(
      this.selectedDuration,
    );
    this.fromDate = range.fromDate;
    this.toDate = range.toDate;
    this.maxFromDate = range.maxFromDate;
    this.maxToDate = range.maxToDate;
  }

  onFromDateChange(): void {
    if (!this.fromDate || this.selectedDuration === 'custom') return;
    this.toDate = this.dateRangeService.adjustDateRangeFromChange(
      this.fromDate,
      this.selectedDuration,
    );
  }

  onToDateChange(): void {
    if (!this.toDate || this.selectedDuration === 'custom') return;
    this.fromDate = this.dateRangeService.adjustDateRangeToChange(
      this.toDate,
      this.selectedDuration,
    );
  }

  onCategoryChange() {
    if (!this.multiSelectMode) {
      // Single-select: keep only the newly added category; prevent full deselect
      const added = this.selectedCategories.find(
        (c) => !this.previousCategories.includes(c),
      );
      this.selectedCategories = added ? [added] : [this.previousCategories[0]];
    }
    this.previousCategories = [...this.selectedCategories];

    if (!this.selectedTenant || this.loading || this.loadingMore) return;
    if (!this.selectedCategories.length) return;
    if (this.dateRangeService.validateDateRange(this.fromDate, this.toDate))
      return;
    this.resetTableAndFilters();
    this.cancelLoad$.next();
    this.transactions = [];
    this.loadTransactions();
  }

  onMultiSelectModeChange() {
    if (!this.multiSelectMode && this.selectedCategories.length > 1) {
      this.selectedCategories = [this.selectedCategories[0]];
      this.previousCategories = [...this.selectedCategories];
      this.onCategoryChange();
    }
  }

  onGetTransactions() {
    const validationError = this.dateRangeService.validateDateRange(
      this.fromDate,
      this.toDate,
    );
    if (validationError) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Date Range',
        detail: validationError,
      });
      return;
    }

    if (!this.selectedTenant) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a tenant to fetch the transactions.',
      });
      return;
    }

    if (!this.selectedCategories.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Category Selected',
        detail: 'Please select at least one transaction category.',
      });
      return;
    }

    this.resetTableAndFilters();

    this.cancelLoad$.next();
    this.transactions = [];
    this.loadTransactions();
  }

  // Clears table filters/sort and the custom filter models so a fresh load
  // never leaves a stale filter active — including on a column that a category
  // switch has since hidden (which would otherwise have no UI to clear it).
  private resetTableAndFilters(): void {
    if (this.dt) {
      this.dt.clear();
      this.dt.sortField = 'epochNanoSeconds';
      this.dt.sortOrder = -1;
    }

    this.expandedRows = {};

    this.transactionIdFilter = '';
    this.sourceFilterValue = [];
    this.destinationFilterValue = [];
    this.approvedFilterValue = null;
  }

  private loadTransactions() {
    let isFirstBatch = true;
    this.loading = true;

    // Snapshot params to prevent stale expand continuations from mixing with new loads
    const fromDate = this.fromDate;
    const toDate = this.toDate;
    const tenantId = this.selectedTenant;
    const categories = [...this.selectedCategories];

    this.transactionService
      .getTransactions(fromDate, toDate, tenantId, categories)
      .pipe(
        expand((data: TransactionResponse) => {
          if (data.cursor && data.hasMore) {
            return this.transactionService.getTransactions(
              fromDate,
              toDate,
              tenantId,
              categories,
              data.cursor,
            );
          } else {
            return EMPTY;
          }
        }),
        takeUntil(merge(this.destroy$, this.cancelLoad$)),
      )
      .subscribe({
        next: (data: TransactionResponse) => {
          // transactionId is not unique per row (request/response/reversal legs
          // share one id), so we must not dedup on it. Stale/overlapping loads
          // are already prevented by the snapshotted params + takeUntil(cancelLoad$).
          this.transactions = [...this.transactions, ...data.items];
          if (isFirstBatch) {
            this.loading = false;
            this.loadingMore = true;
            isFirstBatch = false;
          }
        },
        complete: () => {
          this.loadingMore = false;
        },
        error: (error) => {
          console.error('Error loading transactions:', error);
          this.loading = false;
          this.loadingMore = false;
        },
      });
  }

  cancelLoad(): void {
    this.cancelLoad$.next();
    this.loading = false;
    this.loadingMore = false;
  }

  onGlobalFilterChange(event: Event): void {
    const value =
      (event.target as HTMLInputElement).value?.trim().toLowerCase() || '';
    this.dt.filterGlobal(value, 'contains');
  }

  trackByEpochNano(_index: number, item: any): any {
    return item.epochNanoSeconds;
  }

  onClear(dt: Table) {
    dt.clear();
    dt.sortField = 'epochNanoSeconds';
    dt.sortOrder = -1;
    dt.sortSingle();
  }

  formatJson(jsonString: string | null): string {
    return formatJson(jsonString);
  }

  getFieldValue(transaction: Transaction, col: ColumnConfig): any {
    const value = transaction[col.field as keyof Transaction];

    if (col.pipe === 'currency' && col.pipeArgs) {
      if (typeof value === 'string' && value !== '') {
        const numValue = parseFloat(value);
        return !isNaN(numValue)
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: col.pipeArgs[0],
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(numValue)
          : '';
      }
      return '';
    }

    return value;
  }

  private generateExportFilename(): string {
    const formatDate = (date: Date) => dayjs(date).format('YYYY-MM-DD');
    const formatTime = (date: Date) => dayjs(date).format('HHmm');

    let fromTime: string, toTime: string;

    if (this.selectedDuration === 'custom') {
      fromTime = formatTime(this.fromDate);
      toTime = formatTime(this.toDate);
    } else {
      fromTime = '0000';
      toTime = '2359';
    }

    const fromDateStr = formatDate(this.fromDate);
    const toDateStr = formatDate(this.toDate);

    // Access-pattern (VIEW) segment: label(s) of the selected category tab(s),
    // kept in the canonical categoryOptions order so single- and multi-select
    // filenames are deterministic (e.g. "Terminal Activity_Admin Audit Trails").
    const categorySegment = this.categoryOptions
      .filter((option) => this.selectedCategories.includes(option.value))
      .map((option) => option.label)
      .join('_');
    const categoryPart = categorySegment ? `${categorySegment}_` : '';

    // Timestamp of when the CSV was downloaded.
    const downloadedAt = dayjs().format('YYYY-MM-DD_HHmmss');

    return `${this.selectedTenant || 'unknown'}_transactions_${categoryPart}${fromDateStr}_${fromTime}_${toDateStr}_${toTime}_${downloadedAt}.csv`;
  }

  exportToCSV(): void {
    const dataToExport = this.dt.filteredValue || this.transactions;
    if (!dataToExport || dataToExport.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No data to export',
      });
      return;
    }

    const csvContent = convertTransactionsToCSV(dataToExport, this.visibleColumns);
    const filename = this.generateExportFilename();
    downloadCSV(csvContent, filename);
  }
}
